import React, { useState, useEffect, useRef, useContext } from "react";
import { StyleSheet, ScrollView, Text, TouchableOpacity } from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { replaceDataValues, renameLabel } from "../../utils/common";
import { View } from "../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../components/custom/DataTable/DataTableWithPagination";
import Spinner from "../../components/shared/Spinner";
import useColorScheme from "../../hooks/useColorScheme";
import { userClaimInformation } from "../../models/usersModel";
import _ from "lodash";
import { getDelivery } from "../../services/deliveryService";
import { headers, mdHeaders, smHeaders } from "./utils/deliveryHeaders";
import {
  nestedHeaders as deliveryHeader,
  mdNestedHeaders as deliveryMdHeaders,
  smNestedHeaders as deliverySmHeaders,
} from "./utils/deliveryDetailsHeaders";
import { deliveryModel } from "../../models/reportsModel";
import { filterBasedOnAnyKey } from "../../utils/searchInArrays";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import { useSelector } from "react-redux";
import { handleTableLoader } from "../../utils/common";
import { sortColumns } from "../../utils/sortColumns";
import { UploadAttachmentDeliveryModal } from "./utils/UploadAttachmentDeliveryContent";
import { DeliveryStatusConfirmModal } from "./utils/DeliveryStatusConfirmModal";

export default function DeliverytScreen({ route, navigation }: any) {
  const scrollRef = useRef<ScrollView>(null);
  const isFocused = useIsFocused();
  const colorScheme = useColorScheme();

  const columnsToNotHaveThousandsSeparator: Array<string> = [
    "Document #",
    "Reference",
    "Document # ",
    "Portal Order No",
    "DocNum",
  ];
  const dataToBeConverted: object = {
    /*{ "searchString" : "replaceValue" }*/
    Dispatched: "Shipped",
    "Not Confirmed": "Pending for Payment",
  };

  const { userInfo } = useSelector((state: any) => state.configurations);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [isImageLoading, setImageLoading] = useState<boolean>(true);
  const [deliveryAttachmentModalVisible, setDeliveryAttachmentModalVisible] =
    useState<boolean>(false);
  const [deliveryStatusModal, setDeliveryStatusModal] =
    useState<boolean>(false);
  const [deliveryPageLevelList, setDeliveryPageLevelList] = useState<
    Array<deliveryModel>
  >([]);

  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const [_stateFilter, _setStateFilter] = useState<string>("all");
  const [_dateFilter, _setDateFilter] = useState<string>("Next 7 days");
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedDates, setSelectedDates] = useState<string[]>(["Next 7 days"]);

  const stateFilters = ["all", "NSW", "VIC", "QLD", "WA", "SA", "TAS"];
  const dateFilters = ["All", "Today", "Next 7 days", "Next 14 days", "Next 30 days"];
  const { userRoles } = useSelector((state: any) => state.configurations);

  const { deliveryList, deliveryListFilterCategoryList, isDeliveryListLoaded } =
    useSelector((state: any) => state.delivery);

  const { deliveryItemsList } = useSelector((state: any) => state.items);

  const [selected, setSelected] = useState<deliveryModel>(
    {} as unknown as deliveryModel
  );

  const [selectedDeliveryItem, setSelectedDeliveryItem] =
    useState<deliveryModel>({} as unknown as deliveryModel);

  //Table & Nested Table Headers
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headers
    );

  const [nestedTableHeaders, setNestedTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      deliveryHeader
    );

  const [_isDisplayViewOn, _setIsDisplayViewOn] = useState(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [filterExpanded, setFilterExpanded] = useState<boolean>(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState<"state" | "date" | null>(null);

  const [, setContentOffSet] = useState<number>(0);

  const filterDeliveryByCategory = (
    _deliveryMode: Array<deliveryModel>,
    stateFilter: string,
    dateFilter: string,
    stateFiltersArr?: string[],
    dateFiltersArr?: string[]
  ) => {
    const today = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() - 1
    );
    const tmw = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + 1
    );
    const Nxt7Days = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + 7
    );
    const Nxt14Days = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + 14
    );
    const Nxt30Days = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + 30
    );

    const getDateRange = (filter: string): [Date, Date] | null => {
      switch (filter) {
        case "Today":
          return [today, tmw];
        case "Next 7 days":
          return [today, Nxt7Days];
        case "Next 14 days":
          return [today, Nxt14Days];
        case "Next 30 days":
          return [today, Nxt30Days];
        default:
          return null;
      }
    };

    return _deliveryMode.filter((delivery: deliveryModel) => {
      // Apply state filter (multi-select)
      let matchesState = true;
      const activeStates = stateFiltersArr && stateFiltersArr.length > 0 ? stateFiltersArr : (stateFilter !== "all" ? [stateFilter] : []);
      if (activeStates.length > 0) {
        matchesState = activeStates.includes(delivery.State);
      }

      // Apply date filter (multi-select)
      let matchesDate = true;
      const activeDates = dateFiltersArr && dateFiltersArr.length > 0 ? dateFiltersArr : [dateFilter];
      const requestDate = new Date(
        new Date(delivery.RequestDate).getFullYear(),
        new Date(delivery.RequestDate).getMonth(),
        new Date(delivery.RequestDate).getDate()
      );

      if (activeDates.length > 0) {
        matchesDate = activeDates.some((df) => {
          const range = getDateRange(df);
          if (!range) return true;
          return requestDate >= range[0] && requestDate <= range[1];
        });
      }

      return matchesState && matchesDate;
    });
  };

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedDeliveryList: Array<deliveryModel> = sortColumns(
      deliveryPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders
    );

    setDeliveryPageLevelList(
      filterDeliveryByCategory(_sortedDeliveryList, _stateFilter, _dateFilter, selectedStates, selectedDates)
    );
  };

  /**
   * TODO: NESTED TABLE SORTED Is Disabled,
   * enable when sorting on nested table feature is requested
   */
  // const onHandleNestedSortColumn = (
  //   columnKey: string,
  //   columnParentIdentifier: string
  // ) => {
  //   const _sortedItemsList: Array<itemsModal> = sortColumns(
  //     nestedItemsPageLevelList,
  //     columnKey,
  //     nestedTableHeaders,
  //     setNestedTableHeaders
  //   );

  //   setNestedItemsPageLevelList(_sortedItemsList);
  // };

  const onHandleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  const onHandleRowClick = (row: deliveryModel) => { };

  const fadeTableLoader = (setIsSearched: (e: boolean) => void) => {
    handleTableLoader(false, true, setIsSearched);
  };

  const onHandleRemoveRow = (containerId: string) => { };

  const onHandleSearch = (searchText: string) => {
    displayTableLoader(setIsSearched);

    let searchedDelivery: Array<deliveryModel> = [];
    if (searchText.trim() !== "") {
      /**
       * When SearchBy: Delivery, enter into if block
       * otherwise go for else block
       */
      if (!_isDisplayViewOn) {
        const filteredDelivery = filterBasedOnAnyKey(
          extractIdenticalDelivery(),
          searchText
        );

        searchedDelivery = feedItemsInDelivery(filteredDelivery);
      } else {
        /**
         * Steps to search for items within containers
         * 1: Extract all items from containers & union them
         * 2: Search by keyword in the list of items extracted from containers
         * 3: Group by container_number on the searchedItems
         * 4: Remove the duplicates from array of container_numbers
         * 5: filter the unique containers from the identicalFeededDeliveryWithItems()
         *    & return the list of containers with items matching the search text
         */

        //Step 1
        const itemsFromDelivery = _.map(
          identicalFeededDeliveryWithItems(),
          "items"
        );
        const flattenItems = _.flatten(itemsFromDelivery);

        //Step 2
        const searchedItems = filterBasedOnAnyKey(flattenItems, searchText);

        //Step 3
        const groupedDelivery = Object.keys(_.groupBy(searchedItems, "DocNum"));

        //Step 4
        const uniqueDelivery = _.uniq(groupedDelivery);

        //Step 5
        const filteredDelivery = identicalFeededDeliveryWithItems().filter(
          (delivery) => uniqueDelivery.includes(delivery.DocNum)
        );

        searchedDelivery = filteredDelivery;
      }

      setDeliveryPageLevelList(
        filterDeliveryByCategory(searchedDelivery, _stateFilter, _dateFilter, selectedStates, selectedDates)
      );
    }

    setTimeout(() => {
      fadeTableLoader(setIsSearched);
    }, 2000);
  };

  const extractIdenticalDelivery = () => {
    //process both containers and items list to return filtered containers list
    const _deliveryItemsList: Array<deliveryModel> = deliveryList;
    const groupDelivery = _.groupBy(_deliveryItemsList, (item) => item.DocNum);
    // check here
    return _deliveryItemsList as Array<deliveryModel>;
  };

  const feedItemsInDelivery = (delivery: deliveryModel[]) => {
    const _deliveryItemsList: Array<deliveryModel> = [...deliveryList];

    return delivery.map((_delivery) => {
      return {
        ..._delivery,
        items: _deliveryItemsList.filter(
          (item: deliveryModel) => item.DocNum === _delivery.DocNum
        ),
      };
    });
  };

  const identicalFeededDeliveryWithItems = (): Array<deliveryModel> => {
    return feedItemsInDelivery(extractIdenticalDelivery());
  };

  const displayTableLoader = (
    setIsSearched: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    handleTableLoader(true, false, setIsSearched);
  };

  useEffect(() => {
    setRefresh(true);
  }, [_isDisplayViewOn]);

  useEffect(() => {
    let isMounted: boolean = true;
    const _userInfo = userInfo as userClaimInformation;
    // Avoid keep refreshing table when item is selected
    if (isMounted) {
      if (deliveryPageLevelList.length === 0 || isRefresh) {
        displayTableLoader(setIsSearched);
        _setStateFilter("all");
        _setDateFilter("Next 7 days");
        setSelectedStates([]);
        setSelectedDates(["Next 7 days"]);
        getDelivery(
          "DeliveryReport",
          "",
          _userInfo.team,
          _userInfo.zone_info || "",
          "delivery_report",
          tableHeaders
        );
      }
    }

    setTableHeaders(
      isLargeDevice ? headers : isMediumDevice ? mdHeaders : smHeaders
    );

    setNestedTableHeaders(
      isLargeDevice
        ? deliveryHeader
        : isMediumDevice
          ? deliveryMdHeaders
          : deliverySmHeaders
    );

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isFocused, isRefresh, _dateFilter === "Next 7 days"]);

  useEffect(() => {
    const unique = [
      ...identicalFeededDeliveryWithItems()
        .reduce((map, obj) => {
          if (!map.has(obj.DocNum)) map.set(obj.DocNum, obj);
          return map;
        }, new Map())
        .values(),
    ];
    setDeliveryPageLevelList(filterDeliveryByCategory(unique, _stateFilter, _dateFilter, selectedStates, selectedDates));

    setTimeout(() => {
      fadeTableLoader(setIsSearched);
    }, 2000);
  }, [deliveryList, deliveryItemsList, _stateFilter, _dateFilter, selectedStates, selectedDates]);

  const navigateToReportDetails = (order: any, action: string) => {
    navigation.navigate("DeliveryDetailsScreen", { order });
  };

  return (
    <ScrollView
      style={{ backgroundColor: colorScheme === "light" ? "white" : "white" }}
      onScroll={onHandleScroll}
      ref={scrollRef}
    >
      <View
        style={{ backgroundColor: colorScheme === "light" ? "white" : "white" }}
      >
        <View
          style={[
            styles.container,
            {
              backgroundColor: colorScheme === "light" ? "#f2f2f2" : "#f2f2f2",
            },
          ]}
        >
          <View
            style={[
              styles.containerContent,
              {
                width: isLargeDevice
                  ? "100%"
                  : isMediumDevice
                    ? "100%"
                    : "100%",
              },
            ]}
          >
            {isDeliveryListLoaded ? (
              <View>
                <LabelBannerWithSearchBar
                  titleName={`Total Delivery: ${deliveryPageLevelList.length}`}
                  handleSearch={onHandleSearch}
                  setRefresh={setRefresh}
                  isResetFields={isRefresh}
                />
                {(isSmallDevice || isMobileDevice) ? (
                  <View>
                    <View style={deliveryFilterStyles.mobileFilterRow}>
                      <TouchableOpacity
                        style={[
                          deliveryFilterStyles.mobileDropdownButton,
                          mobileFilterOpen === "date" && deliveryFilterStyles.mobileDropdownButtonActive,
                        ]}
                        onPress={() => setMobileFilterOpen(mobileFilterOpen === "date" ? null : "date")}
                      >
                        <Text style={deliveryFilterStyles.mobileDropdownLabel}>Date: </Text>
                        <Text style={deliveryFilterStyles.mobileDropdownValue}>
                          {selectedDates.length === 0
                            ? "All"
                            : selectedDates.length === 1
                              ? selectedDates[0]
                              : `${selectedDates.length} selected`}
                        </Text>
                        <Text style={deliveryFilterStyles.mobileDropdownArrow}>{mobileFilterOpen === "date" ? "▲" : "▼"}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          deliveryFilterStyles.mobileDropdownButton,
                          mobileFilterOpen === "state" && deliveryFilterStyles.mobileDropdownButtonActive,
                        ]}
                        onPress={() => setMobileFilterOpen(mobileFilterOpen === "state" ? null : "state")}
                      >
                        <Text style={deliveryFilterStyles.mobileDropdownLabel}>State: </Text>
                        <Text style={deliveryFilterStyles.mobileDropdownValue}>
                          {selectedStates.length === 0
                            ? "All"
                            : selectedStates.length === 1
                              ? selectedStates[0]
                              : `${selectedStates.length} selected`}
                        </Text>
                        <Text style={deliveryFilterStyles.mobileDropdownArrow}>{mobileFilterOpen === "state" ? "▲" : "▼"}</Text>
                      </TouchableOpacity>
                    </View>
                    {mobileFilterOpen === "date" && (
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={deliveryFilterStyles.mobileFilterContainer}
                      >
                        {dateFilters.map((category: string, index: number) => {
                          const isActive = category === "All"
                            ? selectedDates.length === 0
                            : selectedDates.includes(category);
                          return (
                            <TouchableOpacity
                              key={`date-${index}`}
                              style={[
                                deliveryFilterStyles.filterButton,
                                isActive && deliveryFilterStyles.activeFilterButton,
                              ]}
                              onPress={() => {
                                if (category === "All") {
                                  setSelectedDates([]);
                                  _setDateFilter("");
                                } else {
                                  const newDates = selectedDates.includes(category)
                                    ? selectedDates.filter((d) => d !== category)
                                    : [...selectedDates, category];
                                  setSelectedDates(newDates);
                                  _setDateFilter(newDates.length === 0 ? "" : newDates.join(","));
                                }
                              }}
                            >
                              <Text style={isActive ? deliveryFilterStyles.activeFilterText : deliveryFilterStyles.filterText}>
                                {category}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    )}
                    {mobileFilterOpen === "state" && (
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={deliveryFilterStyles.mobileFilterContainer}
                      >
                        {stateFilters.map((category: string, index: number) => {
                          const isActive = category === "all"
                            ? selectedStates.length === 0
                            : selectedStates.includes(category);
                          return (
                            <TouchableOpacity
                              key={`state-${index}`}
                              style={[
                                deliveryFilterStyles.filterButton,
                                isActive && deliveryFilterStyles.activeFilterButton,
                              ]}
                              onPress={() => {
                                if (category === "all") {
                                  setSelectedStates([]);
                                  _setStateFilter("all");
                                } else {
                                  const newStates = selectedStates.includes(category)
                                    ? selectedStates.filter((s) => s !== category)
                                    : [...selectedStates, category];
                                  setSelectedStates(newStates);
                                  _setStateFilter(newStates.length === 0 ? "all" : newStates.join(","));
                                }
                              }}
                            >
                              <Text style={isActive ? deliveryFilterStyles.activeFilterText : deliveryFilterStyles.filterText}>
                                {renameLabel(category)}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    )}
                  </View>
                ) : (
                  <View>
                    <View style={deliveryFilterStyles.filterSection}>
                      <View style={deliveryFilterStyles.filterRow}>
                        <Text style={deliveryFilterStyles.filterLabel}>Date</Text>
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={deliveryFilterStyles.filterChipRow}
                        >
                          {dateFilters.map((category: string, index: number) => {
                            const isActive = category === "All"
                              ? selectedDates.length === 0
                              : selectedDates.includes(category);
                            return (
                              <TouchableOpacity
                                key={index}
                                style={[
                                  deliveryFilterStyles.filterButton,
                                  isActive && deliveryFilterStyles.activeFilterButton,
                                ]}
                                onPress={() => {
                                  if (category === "All") {
                                    setSelectedDates([]);
                                    _setDateFilter("");
                                  } else {
                                    const newDates = selectedDates.includes(category)
                                      ? selectedDates.filter((d) => d !== category)
                                      : [...selectedDates, category];
                                    setSelectedDates(newDates);
                                    _setDateFilter(newDates.length === 0 ? "" : newDates.join(","));
                                  }
                                }}
                              >
                                <Text style={isActive ? deliveryFilterStyles.activeFilterText : deliveryFilterStyles.filterText}>
                                  {category}
                                </Text>
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>
                      </View>
                      <View style={deliveryFilterStyles.filterRow}>
                        <Text style={deliveryFilterStyles.filterLabel}>State</Text>
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={deliveryFilterStyles.filterChipRow}
                        >
                          {stateFilters.map((category: string, index: number) => {
                            const isActive = category === "all"
                              ? selectedStates.length === 0
                              : selectedStates.includes(category);
                            return (
                              <TouchableOpacity
                                key={index}
                                style={[
                                  deliveryFilterStyles.filterButton,
                                  isActive && deliveryFilterStyles.activeFilterButton,
                                ]}
                                onPress={() => {
                                  if (category === "all") {
                                    setSelectedStates([]);
                                    _setStateFilter("all");
                                  } else {
                                    const newStates = selectedStates.includes(category)
                                      ? selectedStates.filter((s) => s !== category)
                                      : [...selectedStates, category];
                                    setSelectedStates(newStates);
                                    _setStateFilter(newStates.length === 0 ? "all" : newStates.join(","));
                                  }
                                }}
                              >
                                <Text style={isActive ? deliveryFilterStyles.activeFilterText : deliveryFilterStyles.filterText}>
                                  {renameLabel(category)}
                                </Text>
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>
                      </View>
                    </View>
                  </View>
                )}
                <DataTableWithPagination
                  headers={tableHeaders}
                  rows={replaceDataValues(
                    deliveryPageLevelList,
                    dataToBeConverted
                  )}
                  itemsPerPage={20}
                  isDataLoading={isSearched}
                  useModal={true}
                  nestedHeader={nestedTableHeaders}
                  isRowsExpandable={true}
                  setModalVisible={setModalVisible}
                  handleRowClick={onHandleRowClick}
                  setSelected={setSelected}
                  setSelectedDeliveryItem={setSelectedDeliveryItem}
                  isToExpandAllRows={_isDisplayViewOn}
                  rowKeyValue="DocNum"
                  nestedItemKeyValue="items"
                  isSubDetail={true}
                  isDetail={false}
                  isEdit={false}
                  isDelete={false}
                  columnsToNotHaveThousandSeparator={
                    columnsToNotHaveThousandsSeparator
                  }
                  isDeliveryAttachment={true}
                  showAttachmentIcon={false}
                  hideInfoIcon={isSmallDevice || isMobileDevice}
                  setDeliveryModalVisible={setDeliveryAttachmentModalVisible}
                  setDeliveryStatusModal={setDeliveryStatusModal}
                  handleSortColumn={onHandleSortColumn}
                  handleDeleteRow={function (id: string | number): void {
                    throw new Error("Function not implemented.");
                  }}
                  operationActions={navigateToReportDetails}
                />
              </View>
            ) : (
              <View>
                <Spinner
                  size="small"
                  color="#74BBFF"
                  styles={{ flexDirection: "row", margin: "20%" }}
                />
              </View>
            )}
          </View>
        </View>
        <UploadAttachmentDeliveryModal
          deliveryAttachmentModalVisible={deliveryAttachmentModalVisible}
          setDeliveryAttachmentModalVisible={setDeliveryAttachmentModalVisible}
          selectedItem={selectedDeliveryItem}
        />

        <DeliveryStatusConfirmModal
          modalVisible={deliveryStatusModal}
          setModalVisible={setDeliveryStatusModal}
          selectedItem={selectedDeliveryItem}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    flexGrow: 1,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    justifySelf: "flex-start",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
  fab: {
    bottom: -10,
    position: "absolute",
    zIndex: 1,
  },
});

const deliveryFilterStyles = StyleSheet.create({
  mobileFilterContainer: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  mobileFilterRow: {
    flexDirection: "row",
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 8,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  mobileDropdownButtonActive: {
    borderColor: "#61B8FF",
    backgroundColor: "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6B7280",
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700",
    color: "#1E40AF",
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: "#6B7280",
    marginLeft: 4,
  },
  filterDivider: {
    width: 1,
    backgroundColor: "#D1D5DB",
    marginHorizontal: 6,
    marginVertical: 2,
  },
  filterToggle: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 15,
    paddingVertical: 8,
  },
  filterToggleText: {
    fontSize: 13,
    fontWeight: "700",
    color: "#374151",
    marginRight: 6,
  },
  filterToggleIcon: {
    fontSize: 10,
    color: "#6B7280",
  },
  filterSection: {
    flexDirection: "row",
    paddingHorizontal: 10,
    paddingVertical: 6,
    gap: 150,
    alignItems: "center",
  },
  filterRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: "700",
    color: "#111827",
    marginRight: 10,
    minWidth: 36,
  },
  filterChipRow: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingRight: 8,
  },
  filterButton: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    marginRight: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  activeFilterButton: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterText: {
    fontSize: 13,
    color: "#475569",
    fontWeight: "500",
  },
  activeFilterText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },
});
